import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Columns3, RotateCcw } from "lucide-react";

/**
 * Componente reusable para mostrar/ocultar columnas en tablas.
 *
 * Uso:
 *   const cols = useColumnPicker("lotes-cols", [
 *     { key: "producto", label: "Producto", defaultVisible: true },
 *     { key: "costo", label: "Costo", defaultVisible: false },
 *   ]);
 *
 *   <ColumnPicker config={cols} />     // botón con popover
 *   {cols.isVisible("costo") && <th>Costo</th>}
 */
export function useColumnPicker(storageKey, columns) {
  const fullKey = `colpicker:${storageKey}`;
  // Default visible set: keys con defaultVisible !== false
  const defaultSet = () => new Set(columns.filter(c => c.defaultVisible !== false).map(c => c.key));

  const [visible, setVisible] = useState(() => {
    try {
      const raw = window.localStorage.getItem(fullKey);
      if (!raw) return defaultSet();
      const arr = JSON.parse(raw);
      if (!Array.isArray(arr)) return defaultSet();
      return new Set(arr);
    } catch (_) {
      return defaultSet();
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(fullKey, JSON.stringify(Array.from(visible)));
    } catch (_) { /* ignore */ }
  }, [visible, fullKey]);

  const toggle = (key) => {
    setVisible(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const reset = () => setVisible(defaultSet());

  const isVisible = (key) => visible.has(key);
  const visibleCount = visible.size;

  return { columns, visible, toggle, reset, isVisible, visibleCount, storageKey: fullKey };
}

export function ColumnPicker({ config, label = "Columnas", testId }) {
  if (!config) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-2 border-[#d1dbd6] text-[#15241d] hover:bg-[#e9f2ee]"
          data-testid={testId || "column-picker-btn"}
          title="Mostrar / ocultar columnas"
        >
          <Columns3 size={14} />
          {label}
          <span className="text-[10px] font-mono text-[#5c7368]">({config.visibleCount}/{config.columns.length})</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="end">
        <div className="flex items-center justify-between mb-2 pb-2 border-b border-[#d1dbd6]">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#5c7368]">Mostrar columnas</span>
          <button
            type="button"
            onClick={config.reset}
            className="text-[10px] text-[#205c45] hover:underline flex items-center gap-1"
            data-testid="column-picker-reset"
          >
            <RotateCcw size={11} /> Restaurar
          </button>
        </div>
        <div className="space-y-1.5 max-h-80 overflow-y-auto">
          {config.columns.map((c) => (
            <label
              key={c.key}
              className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-[#f5f8f6] cursor-pointer text-sm select-none"
              data-testid={`column-picker-item-${c.key}`}
            >
              <Checkbox
                checked={config.isVisible(c.key)}
                onCheckedChange={() => config.toggle(c.key)}
                disabled={c.locked}
              />
              <span className="flex-1 text-[#15241d]">{c.label}</span>
              {c.locked && <span className="text-[9px] text-[#8ba397]">fijo</span>}
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default ColumnPicker;
